import { useEffect, useState } from 'react';
import { useInterview, InterviewProvider } from './InterviewContext';
import { useApp } from '../context/useApp';
import { useLanguage } from '../i18n';
import InterviewQuestion from './InterviewQuestion';
import InterviewDocument from './InterviewDocument';
import InterviewExtract from './InterviewExtract';
import InterviewControls from './InterviewControls';
import InterviewTranscript from './InterviewTranscript';
import {
  ArrowLeft,
  Sparkles,
  MoreVertical,
  MessageSquareText,
  FileText,
  ListChecks,
  X as XIcon,
} from 'lucide-react';

interface InterviewWorkspaceProps {
  sessionId?: string;
  onClose: () => void;
  onComplete?: () => void;
}

type MobileTab = 'question' | 'document' | 'extract';

function Inner({ sessionId, onClose, onComplete }: InterviewWorkspaceProps) {
  const { t } = useLanguage();
  const i = t.interview;
  const { setActiveInterviewSessionId } = useApp();
  const { session, workingDoc, extract, step, load, error, clearError } = useInterview();

  const [tab, setTab] = useState<MobileTab>('question');
  const [moreOpen, setMoreOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  useEffect(() => {
    if (sessionId) {
      void load(sessionId);
    }
  }, [sessionId, load]);

  useEffect(() => {
    if (session?.id) {
      setActiveInterviewSessionId(session.id);
    }
  }, [session?.id, setActiveInterviewSessionId]);

  useEffect(() => {
    if (step.kind === 'done' && onComplete) onComplete();
  }, [step.kind, onComplete]);

  useEffect(() => {
    if (error && (error.kind === 'next' || error.kind === 'clarify' || error.kind === 'regenerate')) {
      const t = setTimeout(() => clearError(), 8000);
      return () => clearTimeout(t);
    }
  }, [error, clearError]);

  if (!session && !sessionId) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-slate">No session.</p>
      </div>
    );
  }

  const tabItems: { id: MobileTab; label: string; icon: any }[] = [
    { id: 'question', label: i.tabQuestion || 'Question', icon: MessageSquareText },
    { id: 'document', label: i.tabDocument || 'Document', icon: FileText },
    { id: 'extract', label: i.tabExtract || 'Extract', icon: ListChecks },
  ];

  return (
    <div className="flex h-full flex-col bg-paper">
      <header className="bg-white border-b border-cloud px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <button
            type="button"
            onClick={onClose}
            className="p-2 -ml-1 text-ash hover:text-ink hover:bg-mist transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-ink flex items-center gap-2 truncate">
              <Sparkles size={16} className="text-red-500 shrink-0" />
              {session?.topic || i.sessionTitle}
            </h1>
            <p className="text-xs text-slate truncate hidden sm:block">{i.sessionSubtitle}</p>
          </div>
        </div>

        {/* Desktop controls: full row, hidden on mobile */}
        <div className="hidden lg:flex shrink-0">
          {session && <InterviewControls />}
        </div>

        {/* Mobile controls: More menu */}
        {session && (
          <div className="lg:hidden relative shrink-0">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="p-2 -mr-1 text-ash hover:text-ink hover:bg-mist min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="More"
              aria-expanded={moreOpen}
            >
              {moreOpen ? <XIcon size={20} /> : <MoreVertical size={20} />}
            </button>
            {moreOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-30"
                  aria-label="Close menu"
                  onClick={() => setMoreOpen(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-cloud shadow-lg z-40 py-1">
                  <MobileControlItem
                    onClick={() => {
                      setMoreOpen(false);
                    }}
                    label={i.transcriptTitle}
                    icon={MessageSquareText}
                    onSelect={() => {
                      setTranscriptOpen(true);
                    }}
                  />
                  <MobileControlItem
                    onClick={() => setMoreOpen(false)}
                    label={i.openTranscriptHint || 'Show transcript'}
                    icon={FileText}
                    onSelect={() => setTranscriptOpen(true)}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </header>

      {/* Desktop layout: 3-pane grid. */}
      <div className="hidden lg:grid flex-1 overflow-hidden grid-cols-3 gap-4 p-4">
        <div className="col-span-1 overflow-y-auto space-y-4">
          <InterviewQuestion />
          {session && <InterviewTranscript sessionId={session.id} />}
        </div>
        <div className="col-span-2 grid grid-rows-2 gap-4 min-h-0">
          <InterviewDocument workingDoc={workingDoc} />
          <InterviewExtract extract={extract} />
        </div>
      </div>

      {/* Mobile layout: top tab bar + active pane. */}
      <div className="lg:hidden flex-1 flex flex-col min-h-0">
        <div role="tablist" className="flex border-b border-cloud bg-white shrink-0">
          {tabItems.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(id)}
                className={`flex-1 min-h-[48px] flex items-center justify-center gap-1.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'text-red-600 border-red-500'
                    : 'text-slate border-transparent hover:text-ink hover:bg-mist'
                }`}
              >
                <Icon size={16} />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 pb-24 safe-bottom">
          {tab === 'question' && (
            <div className="space-y-3">
              <InterviewQuestion />
              {session && (
                <>
                  <InterviewControls />
                  <button
                    type="button"
                    onClick={() => setTranscriptOpen(true)}
                    className="w-full min-h-[44px] flex items-center justify-center gap-2 text-sm font-medium text-slate bg-white border border-cloud hover:bg-mist transition-colors"
                  >
                    <MessageSquareText size={16} />
                    {i.transcriptTitle}
                  </button>
                </>
              )}
            </div>
          )}
          {tab === 'document' && <InterviewDocument workingDoc={workingDoc} />}
          {tab === 'extract' && <InterviewExtract extract={extract} />}
        </div>
      </div>

      {/* Mobile transcript sheet */}
      {transcriptOpen && session && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col bg-black/60" onClick={() => setTranscriptOpen(false)}>
          <div
            className="mt-auto max-h-[85vh] bg-white flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-cloud flex items-center justify-between shrink-0 safe-top">
              <h2 className="text-base font-bold text-ink">{i.transcriptTitle}</h2>
              <button
                type="button"
                onClick={() => setTranscriptOpen(false)}
                className="p-2 -mr-2 text-ash hover:text-ink min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close transcript"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              <InterviewTranscript sessionId={session.id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileControlItem({
  onClick,
  label,
  icon: Icon,
  onSelect,
}: {
  onClick: () => void;
  label: string;
  icon: any;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onClick();
        onSelect();
      }}
      className="w-full flex items-center gap-3 px-4 min-h-[44px] text-sm text-ink hover:bg-mist transition-colors"
    >
      <Icon size={16} className="text-slate shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

export default function InterviewWorkspace(props: InterviewWorkspaceProps) {
  return (
    <InterviewProvider sessionId={props.sessionId}>
      <Inner {...props} />
    </InterviewProvider>
  );
}
